import React, { useState, useEffect } from 'react';
import { PlusCircle, Wallet, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { baseurl } from '../../baseurl';
import axios from 'axios';
import './AddFund.css';

const AddFund = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [showPaypal, setShowPaypal] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, processing, success, error
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(5);

  const presets = [10, 25, 50, 100, 250, 500];

  const toggleModal = () => {
    if (status === 'processing') return; 
    setIsOpen(!isOpen);
    setShowPaypal(false);
    setAmount('');
    setStatus('idle');
    setMessage('');
    setCountdown(5);
  };

  useEffect(() => {
    let timer;
    if (status === 'success' && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (status === 'success' && countdown === 0) {
      window.location.reload();
    }
    return () => clearInterval(timer);
  }, [status, countdown]);

  const handlePaymentSuccess = async (details) => {
    // START LOADER: Set status to processing before the API call starts
    setStatus('processing');
    
    const userToken = localStorage.getItem('userToken');

    const payload = {
      amount: amount,
      orderID: details.id,
      status: details.status,
      payerEmail: details.payer.email_address,
      currency: "USD",
      conversionRate: "1 CR = 1 USD",
      fullDetails: details 
    };

    try {
      // The loader stays visible while this post request is pending
      const response = await axios.post(`${baseurl}addCredits`, payload, {
        headers: { Authorization: `Bearer ${userToken}` }
      });

      if (response.data.success) {
        setStatus('success'); // Switch to Success View
        setMessage(response.data.message || `${amount} CR added successfully!`);
      }
    } catch (error) {
      console.error("PAYMENT API ERROR:", error);
      setStatus('error'); // Switch to Error View
      setMessage(error.response?.data?.message || 'Wallet update failed. Please contact support.');
    }
  };

  return (
    <>
      <button className="btn-wallet btn-add-funds" onClick={toggleModal}>
        <PlusCircle size={16} /> Add Credits
      </button>

      <PayPalScriptProvider options={{ "client-id": "AQOVMXorfbOe-fiJvM9LHSx-r4w603ueFFdhN2SCV2qAv_2g-RltYwvAB73wLFhggfcot3OjEAQAPieA" }}>

    

      {isOpen && (
        <div className="af-modal-overlay">
          <div className="af-modal-container">
            <div className="af-modal-header">
              <div className="af-header-title">
                <div className="af-icon-wrapper">
                  <Wallet size={18} color="#7c3aed" />
                </div>
                <h2>Add Credits to Wallet</h2>
              </div>
              {status !== 'processing' && (
                <button className="af-close-x" onClick={toggleModal}>&times;</button>
              )}
            </div>

            <div className="af-modal-body">
              {/* VIEW 1: AMOUNT SELECTION */}
              {status === 'idle' && !showPaypal && (
                <>
                  <div className="af-form-group">
                    <label>Amount to Add (CR)</label>
                    <div className="af-input-wrapper">
                        <input 
                          type="number" 
                          className="af-input" 
                          placeholder="Enter amount"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                        />
                        <span className="af-currency-tag">CR</span>
                    </div>
                  </div>

                  <div className="af-presets-grid">
                    {presets.map((val) => (
                      <button 
                        key={val} 
                        className={`af-preset-btn ${amount === val.toString() ? 'active' : ''}`}
                        onClick={() => setAmount(val.toString())}
                      >
                        {val} CR
                      </button>
                    ))}
                  </div>

                  <div className="af-summary-card">
                    <div className="af-total-row">
                      <span>Total to Pay (USD):</span>
                      <span>${amount || '0.00'}</span>
                    </div>
                  </div>

                  <div className="af-modal-footer">
                    <button className="af-btn-cancel" onClick={toggleModal}>Cancel</button>
                    <button 
                      className="af-btn-submit" 
                      disabled={!amount || parseFloat(amount) <= 0}
                      onClick={() => setShowPaypal(true)}
                    >
                      Continue
                    </button>
                  </div>
                </>
              )}

              {/* VIEW 2: PAYPAL BUTTONS */}
              {status === 'idle' && showPaypal && (
                <div className="paypal-button-container">
                  <div className="af-payment-back" onClick={() => setShowPaypal(false)}>
                    ← Change Amount
                  </div>
                     <PayPalButtons 
                      style={{ layout: "vertical", shape: "rect" }}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [{ amount: { value: amount } }],
                        });
                      }}
                      onApprove={(data, actions) => {
                        return actions.order.capture().then((details) => handlePaymentSuccess(details));
                      }}
                    />
                  
                </div>
              )}

              {/* VIEW 3: LOADING SPINNER (Shows while addCredits API is running) */}
              {status === 'processing' && (
                <div className="af-status-container">
                  <Loader2 className="af-spinner" size={48} />
                  <h3>Verifying Payment...</h3>
                  <p>Please do not close this window while we update your wallet.</p>
                </div>
              )}

              {/* VIEW 4: SUCCESS MESSAGE + COUNTDOWN */}
              {status === 'success' && (
                <div className="af-status-container success">
                  <CheckCircle2 size={60} color="#10b981" />
                  <h3>Credits Added!</h3>
                  <p>{message}</p>
                  <div className="af-reload-box">
                    Reloading page in <strong>{countdown}s</strong>
                  </div>
                </div>
              )}

              {/* VIEW 5: ERROR MESSAGE */}
              {status === 'error' && (
                <div className="af-status-container error">
                  <AlertCircle size={60} color="#ef4444" />
                  <h3>Something went wrong</h3>
                  <p>{message}</p>
                  <button className="af-btn-submit" onClick={() => setStatus('idle')}>
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

        </PayPalScriptProvider>



    </>
  );
};

export default AddFund;